/**
 * UI-6b Task 13 — RecentRecordings widget renderer.
 *
 * Top-N most recent call recordings from the last 24h. Data from
 * `/recordings?limit=<n>&fromDate=<24h-ago>`. The list endpoint returns
 * recordings ordered by createdAt DESC, so the response is already in the
 * order we want to display.
 *
 * Response shape (from apps/api/src/routes/recordings/list.ts):
 *   { recordings: Array<{ id, callId, durationSec, createdAt, agentId, ... }>,
 *     total, limit, offset }
 *
 * Note on agent identity: the list endpoint returns `agentId` (uuid string
 * or null) but NOT `agentName` — name resolution is intentionally
 * client-side per the recordings route docs. For this widget we display a
 * truncated id (`a:<first-8>`) when available, "—" otherwise, to keep the
 * row dense. Phase 2 can wire in a name-resolver once the dashboards
 * area gains a shared agent-name hook.
 */
import { useEffect, useState, type JSX } from 'react'
import { api } from '../../../../api/client.js'
import { formatDuration, formatRelativeTime } from '../../../../utils/formatDate.js'

export interface RecentRecordingsConfig {
  limit: number
}

interface RecordingRow {
  id: string
  callId: string
  durationSec: number
  createdAt: string
  agentId: string | null
  queueId: string | null
}

interface RecordingsResponse {
  recordings: RecordingRow[]
  total: number
  limit: number
  offset: number
}

function buildFromDate(): string {
  return new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()
}

function clampLimit(n: number): number {
  if (!Number.isFinite(n) || n < 1) return 5
  if (n > 20) return 20
  return Math.floor(n)
}

function agentLabel(agentId: string | null): string {
  if (!agentId) return '—'
  return `a:${agentId.slice(0, 8)}`
}

export function RecentRecordingsRenderer({
  config,
}: {
  config: RecentRecordingsConfig
}): JSX.Element {
  const [rows, setRows] = useState<RecordingRow[] | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  const limit = clampLimit(config.limit)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    const query = new URLSearchParams({
      limit: String(limit),
      fromDate: buildFromDate(),
    })

    api
      .get<RecordingsResponse>(`/recordings?${query.toString()}`)
      .then((resp) => {
        if (!cancelled) setRows(resp.recordings)
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load recordings')
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return (): void => {
      cancelled = true
    }
  }, [limit])

  if (loading) {
    return (
      <div data-testid="recent-recordings-loading" className="text-sm text-gray-500">
        Loading recent recordings…
      </div>
    )
  }
  if (error) {
    return (
      <div data-testid="recent-recordings-error" className="text-sm text-red-600">
        {error}
      </div>
    )
  }
  if (!rows || rows.length === 0) {
    return (
      <div data-testid="recent-recordings-empty" className="text-sm text-gray-500">
        No recordings in the last 24 hours.
      </div>
    )
  }

  return (
    <div data-testid="recent-recordings-widget" className="flex h-full w-full flex-col">
      <div className="px-1 pb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
        Recent recordings
      </div>
      <ul className="flex flex-col gap-1 overflow-auto">
        {rows.map((r) => (
          <li
            key={r.id}
            data-testid="recent-recordings-row"
            className="flex items-center justify-between gap-2 rounded border border-gray-100 px-2 py-1 text-sm"
          >
            <span className="font-mono text-xs text-gray-700">{agentLabel(r.agentId)}</span>
            <span className="tabular-nums text-xs text-gray-600">
              {formatDuration(r.durationSec * 1000)}
            </span>
            <span className="text-xs text-gray-500">{formatRelativeTime(r.createdAt)}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}
